import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as RunbookIcon,
  PlayArrow as PlayIcon,
  RestartAlt as RestartIcon,
  Sync as SyncIcon,
  FactCheck as PreflightIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import {
  fetchEdgeHealth,
  fetchOpsRunbookTemplates,
  executeRunbookServer,
  type RecommendedAction,
  type OpsRunbookTemplate,
} from '../services/opsCopilot';
import { useAuth } from '../contexts/AuthContext';
import { getCapabilitiesFromUser, hasCapability } from '../auth/access';

export type SuggestedActionsContext = 'bringup' | 'preflight' | 'device_control';

type ActionResult =
  | { level: 'success' | 'warning' | 'error'; message: string; details?: any }
  | null;

type SuggestedAction = {
  id: string;
  title: string;
  detail?: string;
  icon?: React.ReactNode;
  disabledReason?: string;
  onClick: () => Promise<void> | void;
};

export const SuggestedActionsRail: React.FC<{
  context: SuggestedActionsContext;
  deviceId?: string;
}> = ({ context, deviceId }) => {
  const navigate = useNavigate();
  const { effectiveUser: user } = useAuth();
  const caps = useMemo(() => getCapabilitiesFromUser(user), [user]);
  const canWriteDevices = hasCapability(caps, 'devices:write');
  const canWriteDeployments = hasCapability(caps, 'deployments:write');
  const canWriteIncidents = hasCapability(caps, 'incidents:write');
  const canWriteAudit = hasCapability(caps, 'audit:write');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResult>(null);
  const [recommendedActions, setRecommendedActions] = useState<RecommendedAction[]>([]);
  const [templates, setTemplates] = useState<OpsRunbookTemplate[]>([]);
  const [healthLoading, setHealthLoading] = useState(false);

  const normalizedDeviceId = String(deviceId || '').trim();
  const hasDevice = Boolean(normalizedDeviceId);

  useEffect(() => {
    if (!normalizedDeviceId) {
      setRecommendedActions([]);
      return;
    }
    let mounted = true;
    setHealthLoading(true);
    Promise.all([fetchEdgeHealth(normalizedDeviceId), fetchOpsRunbookTemplates()])
      .then(([health, tpls]) => {
        if (!mounted) return;
        setTemplates(Array.isArray(tpls) ? tpls : []);
        const ra = health?.recommended_actions;
        setRecommendedActions(Array.isArray(ra) ? ra : []);
      })
      .finally(() => {
        if (mounted) setHealthLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [normalizedDeviceId]);

  const runRecommendedAction = async (a: RecommendedAction) => {
    if (!canWriteAudit) {
      setResult({
        level: 'error',
        message: 'Access denied. Required capability: audit:write.',
      });
      return;
    }
    const t = templates.find((x) => x.id === a.action_id);
    if (!t) {
      setResult({ level: 'error', message: `Runbook ${a.action_id} not found` });
      return;
    }
    setResult(null);
    setBusyId(a.action_id);
    try {
      const prefill: Record<string, any> = { ...(a.default_inputs || {}) };
      if (a.why?.length) prefill.reason = a.why.join('; ');
      if (!prefill.device_id) prefill.device_id = normalizedDeviceId;
      const ex = await executeRunbookServer(t, {
        device_id: prefill.device_id || normalizedDeviceId,
        actor: user?.username,
        params: prefill,
      });
      setResult({
        level: ex.status === 'succeeded' ? 'success' : 'warning',
        message: ex.status === 'succeeded' ? `${t.title} completed.` : `${t.title} finished with status: ${ex.status}`,
        details: { execution_id: ex.execution_id },
      });
      if (a.post_action_navigation) {
        navigate(a.post_action_navigation);
      }
    } catch (e: any) {
      setResult({
        level: 'error',
        message: e?.response?.data?.error || e?.message || 'Runbook failed',
        details: e?.response?.data,
      });
    } finally {
      setBusyId(null);
    }
  };

  const actions = useMemo((): SuggestedAction[] => {
    const base: SuggestedAction[] = [
      {
        id: 'open_runbooks',
        title: 'Open Runbooks',
        detail: 'Deterministic, auditable templates (bring-up, convergence, rollback).',
        icon: <RunbookIcon fontSize="small" />,
        onClick: () => {
          const url = new URL('/ops/runbooks', window.location.origin);
          if (hasDevice) url.searchParams.set('device', normalizedDeviceId);
          navigate(url.pathname + url.search);
        },
      },
    ];

    const runPreflight: SuggestedAction = {
      id: 'preflight_run',
      title: 'Run strict preflight now',
      detail: 'Runs on edge; use as a gate before deploy or after remediation.',
      icon: <PreflightIcon fontSize="small" />,
      disabledReason: !hasDevice ? 'Select a device first.' : (!canWriteDeployments ? 'Requires deployments:write.' : undefined),
      onClick: async () => {
        if (!hasDevice) return;
        const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(normalizedDeviceId)}/edge/preflight/run`, {
          cameras: true,
        });
        setResult({
          level: res?.data?.preflight_passed === true || res?.data?.ok === true ? 'success' : 'warning',
          message: 'Preflight requested.',
          details: res?.data,
        });
      },
    };

    const restartOrchestrator: SuggestedAction = {
      id: 'orchestrator_restart',
      title: 'Restart orchestrator',
      detail: 'Safe first remediation step for stuck pipelines.',
      icon: <RestartIcon fontSize="small" />,
      disabledReason: !hasDevice ? 'Select a device first.' : (!canWriteDevices ? 'Requires devices:write.' : undefined),
      onClick: async () => {
        if (!hasDevice) return;
        const res = await api.post(
          `/api/v1/pipeline/devices/${encodeURIComponent(normalizedDeviceId)}/edge/orchestrator/restart`,
          {},
        );
        setResult({
          level: res?.data?.ok === true ? 'success' : 'warning',
          message: res?.data?.ok === true ? 'Orchestrator restart requested.' : 'Orchestrator restart requested (check response).',
          details: res?.data,
        });
      },
    };

    const syncDesiredState: SuggestedAction = {
      id: 'desired_state_sync',
      title: 'Sync desired state',
      detail: 'Re-apply desired state to converge edge to cloud.',
      icon: <SyncIcon fontSize="small" />,
      disabledReason: !hasDevice ? 'Select a device first.' : (!canWriteIncidents ? 'Requires incidents:write.' : undefined),
      onClick: async () => {
        if (!hasDevice) return;
        const res = await api.post(
          `/api/v1/pipeline/devices/${encodeURIComponent(normalizedDeviceId)}/edge/desired_state/sync`,
          {},
        );
        setResult({
          level: res?.data?.ok === true ? 'success' : 'warning',
          message: res?.data?.ok === true ? 'Desired-state sync requested.' : 'Desired-state sync requested (check response).',
          details: res?.data,
        });
      },
    };

    const openBringup: SuggestedAction = {
      id: 'open_bringup',
      title: 'Open bring-up',
      detail: 'Single commissioning flow (onboard + strict gate).',
      icon: <PlayIcon fontSize="small" />,
      onClick: () => {
        if (hasDevice) navigate(`/bringup?device=${encodeURIComponent(normalizedDeviceId)}`);
        else navigate('/bringup');
      },
    };

    const openPreflight: SuggestedAction = {
      id: 'open_preflight',
      title: 'Open advanced preflight',
      detail: 'Diagnostics + direct checks.',
      icon: <PreflightIcon fontSize="small" />,
      onClick: () => {
        if (hasDevice) navigate(`/preflight?device=${encodeURIComponent(normalizedDeviceId)}`);
        else navigate('/preflight');
      },
    };

    const openDeviceControl: SuggestedAction = {
      id: 'open_device_control',
      title: 'Open device control',
      detail: 'Per-camera pipeline control, rollback, proof.',
      icon: <PlayIcon fontSize="small" />,
      disabledReason: !hasDevice ? 'Select a device first.' : (!canWriteDevices ? 'Requires devices:write.' : undefined),
      onClick: () => {
        if (!hasDevice) return;
        navigate(`/devices/${encodeURIComponent(normalizedDeviceId)}/control`);
      },
    };

    const productionDeployEnterprise: SuggestedAction = {
      id: 'enterprise_production_deploy',
      title: 'Production deploy (Enterprise)',
      detail: 'Model bundle + strict gate + acceptance (not day-2 OTA).',
      icon: <PlayIcon fontSize="small" />,
      disabledReason: !hasDevice
        ? 'Select a device first.'
        : !canWriteDeployments
          ? 'Requires deployments:write.'
          : undefined,
      onClick: () => {
        if (!hasDevice) return;
        navigate(
          `/devices/${encodeURIComponent(normalizedDeviceId)}/control#enterprise-production-deploy`,
        );
      },
    };

    if (context === 'bringup') {
      return [
        ...base,
        productionDeployEnterprise,
        openPreflight,
        openDeviceControl,
        runPreflight,
        restartOrchestrator,
      ];
    }

    if (context === 'preflight') {
      return [
        ...base,
        openBringup,
        openDeviceControl,
        runPreflight,
        restartOrchestrator,
      ];
    }

    // device_control
    return [
      ...base,
      productionDeployEnterprise,
      runPreflight,
      syncDesiredState,
      restartOrchestrator,
      openPreflight,
    ];
  }, [context, hasDevice, navigate, normalizedDeviceId, canWriteDeployments, canWriteDevices, canWriteIncidents]);

  const runAction = async (a: SuggestedAction) => {
    const disabled = Boolean(a.disabledReason);
    if (disabled) return;
    setResult(null);
    try {
      const ret = a.onClick();
      if (ret && typeof (ret as any).then === 'function') {
        setBusyId(a.id);
        await ret;
      }
    } catch (e: any) {
      setResult({
        level: 'error',
        message: e?.response?.data?.error || e?.message || 'Action failed',
        details: e?.response?.data,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        position: { lg: 'sticky' },
        top: { lg: 88 },
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Suggested Actions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Deterministic actions mapped to trusted control-plane endpoints.
      </Typography>

      {healthLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!healthLoading && recommendedActions.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            From device health
          </Typography>
          {!canWriteAudit && (
            <Alert severity="info">
              Recommended runbooks require <strong>audit:write</strong>. This user can view runbooks but cannot execute them.
            </Alert>
          )}
          {recommendedActions.map((a) => {
            const t = templates.find((x) => x.id === a.action_id);
            const label = t?.title || a.action_id;
            const busy = busyId === a.action_id;
            return (
              <Button
                key={a.action_id}
                variant="contained"
                size="small"
                fullWidth
                disabled={busy || !canWriteAudit}
                startIcon={busy ? <CircularProgress size={14} /> : <PlayIcon />}
                onClick={() => runRecommendedAction(a)}
                sx={{ textTransform: 'none' }}
              >
                {label}
              </Button>
            );
          })}
        </Stack>
      )}
      {!healthLoading && recommendedActions.length > 0 && <Divider sx={{ my: 2 }} />}

      <Stack spacing={1.25}>
        {actions.map((a) => (
          <Box key={a.id}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={a.icon}
              onClick={() => void runAction(a)}
              disabled={Boolean(a.disabledReason) || busyId === a.id}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {busyId === a.id ? 'Running…' : a.title}
            </Button>
            {a.detail && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {a.disabledReason ? `${a.detail} (${a.disabledReason})` : a.detail}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {result && (
        <Alert severity={result.level} onClose={() => setResult(null)}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {result.message}
          </Typography>
          {result.details && (
            <pre style={{ margin: 0, marginTop: 6, whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {JSON.stringify(result.details, null, 2)}
            </pre>
          )}
        </Alert>
      )}
    </Paper>
  );
};

export default SuggestedActionsRail;
